import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, Search, X, Filter, RefreshCw, Download, UserPlus, Edit2,
  Trash2, Mail, MapPin, Globe, Link2, User, UserCheck, AlertCircle,
  ChevronLeft, ChevronRight, Grid, List as ListIcon, Loader, Save,
  ArrowUpDown, Check, Instagram, Calendar, ExternalLink, Send, Eye,
  CheckCircle, AlertTriangle, MessageSquare, Tag
} from 'lucide-react';
import { supabase } from '../../utils/supabaseClient';
import * as XLSX from 'xlsx';
import RichTextEditor from '../ui/RichTextEditor';
import '../../styles/ProspectsAdmin.css';

// VibrantCast organization ID
const VIBRANTCAST_ORG_ID = 2;

const ProspectsAdmin = () => {
  const navigate = useNavigate();
  
  // Data state
  const [prospects, setProspects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // UI state
  const [searchQuery, setSearchQuery] = useState('');
  const [linkedFilter, setLinkedFilter] = useState('all'); // 'all', 'linked', 'unlinked'
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [viewMode, setViewMode] = useState('list');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [activeDropdown, setActiveDropdown] = useState(null);
  
  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingProspect, setEditingProspect] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
  const [selectedProspects, setSelectedProspects] = useState(new Set());
  const [linkingProspectId, setLinkingProspectId] = useState(null);
  const [showDownloadDropdown, setShowDownloadDropdown] = useState(false);
  const [exporting, setExporting] = useState(false);
  const downloadDropdownRef = useRef(null);
  
  // Form state
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    instagram: '',
    website: '',
    note: ''
  });
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  
  // Stats
  const [stats, setStats] = useState({
    total: 0,
    linked: 0,
    unlinked: 0
  });

  // Tab state for prospects vs communications
  const [activeTab, setActiveTab] = useState('prospects'); // 'prospects' or 'communications'
  
  // Communications state
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [orgMembers, setOrgMembers] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [targetAudience, setTargetAudience] = useState('all'); // 'all', 'unlinked', 'linked'
  const [testMode, setTestMode] = useState(true);
  const [selectedTestAdminIds, setSelectedTestAdminIds] = useState([]);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailResults, setEmailResults] = useState(null);
  const [showPreview, setShowPreview] = useState(false);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  
  // Custom message state (when not using a template)
  const [useCustomMessage, setUseCustomMessage] = useState(false);
  const [customSubject, setCustomSubject] = useState('');
  const [customHtmlBody, setCustomHtmlBody] = useState('');

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`;
    if (diffDays < 365) return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatFullDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  // Load prospects
  const loadProspects = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data, error: fetchError } = await supabase
        .from('user_prospects')
        .select('*')
        .order('created_at', { ascending: false });

      if (fetchError) throw fetchError;

      setProspects(data || []);
      
      // Calculate stats
      const newStats = {
        total: data?.length || 0,
        linked: data?.filter(p => p.uuid).length || 0,
        unlinked: data?.filter(p => !p.uuid).length || 0
      };
      setStats(newStats);

    } catch (err) {
      console.error('Error loading prospects:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadProspects();
    loadEmailTemplates();
    loadOrgMembers();
  }, []);

  // Load email templates for VibrantCast organization
  const loadEmailTemplates = async () => {
    try {
      setLoadingTemplates(true);
      const { data: templates, error } = await supabase
        .from('organization_email_templates')
        .select('*')
        .eq('organization_id', VIBRANTCAST_ORG_ID)
        .eq('is_active', true)
        .order('template_key');

      if (error) throw error;
      setEmailTemplates(templates || []);
      console.log(`Loaded ${templates?.length || 0} email templates for VibrantCast`);
    } catch (err) {
      console.error('Error loading email templates:', err);
    } finally {
      setLoadingTemplates(false);
    }
  };

  // Load organization members for test mode
  const loadOrgMembers = async () => {
    try {
      const { data: members, error } = await supabase
        .from('organization_members')
        .select('id, user_uuid, role, users:user_uuid(first_name, last_name, email)')
        .eq('organization_id', VIBRANTCAST_ORG_ID)
        .eq('is_active', true)
        .in('role', ['owner', 'admin', 'editor']);
      
      if (!error && members) {
        setOrgMembers(members);
        console.log(`Loaded ${members.length} org members for test mode`);
      }
    } catch (err) {
      console.error('Error loading org members:', err);
    }
  };

  // Get recipients based on target audience
  const getTargetRecipients = () => {
    switch (targetAudience) {
      case 'linked':
        return prospects.filter(p => p.uuid);
      case 'unlinked':
        return prospects.filter(p => !p.uuid);
      case 'all':
      default:
        return prospects;
    }
  };

  // Get selected template details
  const getSelectedTemplateDetails = () => {
    return emailTemplates.find(t => t.template_key === selectedTemplate);
  };

  // Preview email with sample data
  const getPreviewHtml = (htmlBody) => {
    if (!htmlBody) return '';
    
    // Sample preview with first prospect or placeholder
    const sampleProspect = getTargetRecipients()[0] || {
      first_name: 'Sample',
      last_name: 'Prospect',
      email: 'sample@example.com'
    };
    
    return htmlBody
      .replace(/\{artist_name\}/g, `${sampleProspect.first_name} ${sampleProspect.last_name}`)
      .replace(/\{first_name\}/g, sampleProspect.first_name || 'Artist')
      .replace(/\{organization_name\}/g, 'VibrantCast')
      .replace(/\{contact_email\}/g, 'curator@vibrantcast.com');
  };

  // Send email communication
  const handleSendEmail = async () => {
    // Validate based on mode
    if (useCustomMessage) {
      if (!customSubject.trim()) {
        alert('Please enter an email subject');
        return;
      }
      if (!customHtmlBody.trim()) {
        alert('Please enter email content');
        return;
      }
    } else {
      if (!selectedTemplate) {
        alert('Please select an email template');
        return;
      }
    }

    const recipients = getTargetRecipients();
    if (recipients.length === 0) {
      alert('No prospects match the selected audience');
      return;
    }

    if (testMode && selectedTestAdminIds.length === 0) {
      alert('Please select at least one test recipient');
      return;
    }

    try {
      setSendingEmail(true);
      setEmailResults(null);

      // Build target users list for the edge function
      // For prospects, we need to create a compatible format
      // In test mode, build target_users from selected org members
      let targetUsers;
      if (testMode) {
        // Get the selected org members and build target_users from them
        const selectedMembers = orgMembers.filter(m => selectedTestAdminIds.includes(m.id));
        targetUsers = selectedMembers.map(m => ({
          uuid: m.user_uuid,
          email: m.users?.email || '',
          first_name: m.users?.first_name || '',
          last_name: m.users?.last_name || ''
        }));
      } else {
        targetUsers = recipients.map(p => ({
          uuid: p.uuid || `prospect_${p.id}`,
          email: p.email,
          first_name: p.first_name,
          last_name: p.last_name,
          is_prospect: !p.uuid
        }));
      }

      const payload = {
        organization_id: VIBRANTCAST_ORG_ID,
        template_key: useCustomMessage ? 'custom_message' : selectedTemplate,
        target_audience: 'specific_users',  // Use specific_users for both - we're providing the list
        target_users: targetUsers,
        test_mode: testMode,
        test_admin_ids: testMode ? selectedTestAdminIds : undefined,
        // Pass custom message content via merge vars (template uses {custom_subject} and {custom_body})
        custom_merge_vars: useCustomMessage ? {
          custom_subject: customSubject,
          custom_body: customHtmlBody
        } : {}
      };

      console.log('Sending prospect email:', payload);

      const response = await fetch('https://api.vibrantcast.com/functions/v1/organization_email_hub', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const result = await response.json();
      console.log('Email send result:', result);
      setEmailResults(result);

    } catch (err) {
      console.error('Error sending email:', err);
      setEmailResults({ success: false, error: err.message });
    } finally {
      setSendingEmail(false);
    }
  };

  // Close download dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (downloadDropdownRef.current && !downloadDropdownRef.current.contains(event.target)) {
        setShowDownloadDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Prepare export data
  const prepareExportData = () => {
    return filteredProspects.map(prospect => ({
      'First Name': prospect.first_name || '',
      'Last Name': prospect.last_name || '',
      'Email': prospect.email || '',
      'Instagram': prospect.instagram || '',
      'Website': prospect.website || '',
      'Notes': prospect.note || '',
      'Linked to User': prospect.uuid ? 'Yes' : 'No',
      'Created': prospect.created_at ? formatFullDate(prospect.created_at) : ''
    }));
  };

  // Export to CSV
  const exportToCSV = () => {
    try {
      setExporting(true);
      const data = prepareExportData();
      
      if (data.length === 0) {
        alert('No prospects to export');
        return;
      }

      const headers = Object.keys(data[0]);
      const csvRows = [
        headers.join(','),
        ...data.map(row => 
          headers.map(header => {
            let cell = row[header] || '';
            if (cell.toString().includes(',') || cell.toString().includes('"') || cell.toString().includes('\n')) {
              cell = `"${cell.toString().replace(/"/g, '""')}"`;
            }
            return cell;
          }).join(',')
        )
      ];
      
      const csvContent = csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const filename = `prospects_${new Date().toISOString().split('T')[0]}.csv`;
      
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      setShowDownloadDropdown(false);
    } catch (err) {
      console.error('Error exporting CSV:', err);
      alert('Error exporting to CSV: ' + err.message);
    } finally {
      setExporting(false);
    }
  };

  // Export to Excel
  const exportToExcel = () => {
    try {
      setExporting(true);
      const data = prepareExportData();
      
      if (data.length === 0) {
        alert('No prospects to export');
        return;
      }

      const worksheet = XLSX.utils.json_to_sheet(data);
      const workbook = XLSX.utils.book_new();
      
      const colWidths = [
        { wch: 15 }, // First Name
        { wch: 15 }, // Last Name
        { wch: 30 }, // Email
        { wch: 20 }, // Instagram
        { wch: 25 }, // Website
        { wch: 30 }, // Notes
        { wch: 15 }, // Linked
        { wch: 12 }, // Created
      ];
      worksheet['!cols'] = colWidths;
      
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Prospects');
      
      const filename = `prospects_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(workbook, filename);
      
      setShowDownloadDropdown(false);
    } catch (err) {
      console.error('Error exporting Excel:', err);
      alert('Error exporting to Excel: ' + err.message);
    } finally {
      setExporting(false);
    }
  };

  // Filter and sort prospects
  const filteredProspects = useMemo(() => {
    let filtered = [...prospects];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p => 
        p.first_name?.toLowerCase().includes(query) ||
        p.last_name?.toLowerCase().includes(query) ||
        p.email?.toLowerCase().includes(query) ||
        p.instagram?.toLowerCase().includes(query) ||
        p.note?.toLowerCase().includes(query)
      );
    }

    // Linked filter
    if (linkedFilter === 'linked') {
      filtered = filtered.filter(p => p.uuid);
    } else if (linkedFilter === 'unlinked') {
      filtered = filtered.filter(p => !p.uuid);
    }

    // Sort
    filtered.sort((a, b) => {
      let aVal, bVal;
      
      switch (sortBy) {
        case 'name':
        case 'last_name':
          aVal = `${a.last_name || ''} ${a.first_name || ''}`.toLowerCase();
          bVal = `${b.last_name || ''} ${b.first_name || ''}`.toLowerCase();
          break;
        case 'email':
          aVal = a.email?.toLowerCase() || '';
          bVal = b.email?.toLowerCase() || '';
          break;
        case 'created_at':
          aVal = a.created_at || '';
          bVal = b.created_at || '';
          break;
        default:
          aVal = a[sortBy] || '';
          bVal = b[sortBy] || '';
      }

      if (sortOrder === 'asc') {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      } else {
        return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
      }
    });

    return filtered;
  }, [prospects, searchQuery, linkedFilter, sortBy, sortOrder]);

  // Pagination
  const totalPages = Math.ceil(filteredProspects.length / itemsPerPage);
  const paginatedProspects = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredProspects.slice(start, start + itemsPerPage);
  }, [filteredProspects, currentPage, itemsPerPage]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, linkedFilter]);

  // Form validation
  const validateForm = () => {
    const errors = {};
    
    if (!formData.first_name?.trim()) {
      errors.first_name = 'First name is required';
    }
    if (!formData.last_name?.trim()) {
      errors.last_name = 'Last name is required';
    }
    if (!formData.email?.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email format';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Add new prospect
  const handleAddProspect = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);

      const { data, error: insertError } = await supabase
        .from('user_prospects')
        .insert({
          first_name: formData.first_name.trim(),
          last_name: formData.last_name.trim(),
          email: formData.email.trim().toLowerCase(),
          instagram: formData.instagram?.trim() || null,
          website: formData.website?.trim() || null,
          note: formData.note?.trim() || null
        })
        .select()
        .single();

      if (insertError) throw insertError;

      setProspects(prev => [data, ...prev]);
      setShowAddModal(false);
      resetForm();
      loadProspects();
      
    } catch (err) {
      console.error('Error adding prospect:', err);
      setFormErrors({ submit: err.message });
    } finally {
      setSaving(false);
    }
  };

  // Update prospect
  const handleUpdateProspect = async () => {
    if (!validateForm() || !editingProspect) return;

    try {
      setSaving(true);

      const { data, error: updateError } = await supabase
        .from('user_prospects')
        .update({
          first_name: formData.first_name.trim(),
          last_name: formData.last_name.trim(),
          email: formData.email.trim().toLowerCase(),
          instagram: formData.instagram?.trim() || null,
          website: formData.website?.trim() || null,
          note: formData.note?.trim() || null
        })
        .eq('id', editingProspect.id)
        .select()
        .single();

      if (updateError) throw updateError;

      setProspects(prev => prev.map(p => p.id === editingProspect.id ? data : p));
      setShowEditModal(false);
      setEditingProspect(null);
      resetForm();
      
    } catch (err) {
      console.error('Error updating prospect:', err);
      setFormErrors({ submit: err.message });
    } finally {
      setSaving(false);
    }
  };

  // Delete prospect
  const handleDeleteProspect = async (prospect) => {
    try {
      const { error: deleteError } = await supabase
        .from('user_prospects')
        .delete()
        .eq('id', prospect.id);

      if (deleteError) throw deleteError;

      setProspects(prev => prev.filter(p => p.id !== prospect.id));
      setShowDeleteConfirm(null);
      loadProspects();
      
    } catch (err) {
      console.error('Error deleting prospect:', err);
      setError(err.message);
    }
  };

  // Link prospect to existing user by email
  const handleLinkToUser = async (prospect) => {
    try {
      setLinkingProspectId(prospect.id);
      
      // Find user with matching email
      const { data: user, error: userError } = await supabase
        .from('users')
        .select('uuid, first_name, last_name')
        .ilike('email', prospect.email)
        .single();

      if (userError || !user) {
        alert(`No user found with email: ${prospect.email}`);
        return;
      }

      // Update prospect with user uuid
      const { error: updateError } = await supabase
        .from('user_prospects')
        .update({ uuid: user.uuid })
        .eq('id', prospect.id);

      if (updateError) throw updateError;

      // Refresh prospects
      await loadProspects();
      
    } catch (err) {
      console.error('Error linking prospect:', err);
      alert('Error linking prospect: ' + err.message);
    } finally {
      setLinkingProspectId(null);
    }
  };

  // Open edit modal
  const openEditModal = (prospect) => {
    setEditingProspect(prospect);
    setFormData({
      first_name: prospect.first_name || '',
      last_name: prospect.last_name || '',
      email: prospect.email || '',
      instagram: prospect.instagram || '',
      website: prospect.website || '',
      note: prospect.note || ''
    });
    setShowEditModal(true);
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      first_name: '',
      last_name: '',
      email: '',
      instagram: '',
      website: '',
      note: ''
    });
    setFormErrors({});
  };

  // Toggle prospect selection
  const toggleProspectSelection = (prospectId) => {
    setSelectedProspects(prev => {
      const newSet = new Set(prev);
      if (newSet.has(prospectId)) {
        newSet.delete(prospectId);
      } else {
        newSet.add(prospectId);
      }
      return newSet;
    });
  };

  // Select/deselect all visible prospects
  const toggleSelectAll = () => {
    if (selectedProspects.size === paginatedProspects.length) {
      setSelectedProspects(new Set());
    } else {
      setSelectedProspects(new Set(paginatedProspects.map(p => p.id)));
    }
  };

  // Get initials for avatar
  const getInitials = (prospect) => {
    const first = prospect.first_name?.[0] || '';
    const last = prospect.last_name?.[0] || '';
    return (first + last).toUpperCase() || '?';
  };

  // Render linked badge
  const LinkedBadge = ({ prospect }) => {
    if (prospect.uuid) {
      return (
        <span className="prospect-linked-badge linked">
          <UserCheck size={12} />
          <span>Linked User</span>
        </span>
      );
    }
    return (
      <span className="prospect-linked-badge unlinked">
        <User size={12} />
        <span>Prospect Only</span>
      </span>
    );
  };

  // Prospect card for grid view
  const ProspectCard = ({ prospect }) => (
    <div 
      className={`prospect-card ${selectedProspects.has(prospect.id) ? 'selected' : ''}`}
      onClick={() => toggleProspectSelection(prospect.id)}
    >
      <div className="prospect-card-header">
        <div className="prospect-avatar-wrapper">
          <div className="prospect-avatar-placeholder">
            {getInitials(prospect)}
          </div>
          {prospect.uuid && (
            <div className="prospect-linked-indicator">
              <Link2 size={10} />
            </div>
          )}
        </div>
        
        <div className="prospect-card-actions">
          <button
            className="prospect-action-btn"
            onClick={(e) => {
              e.stopPropagation();
              openEditModal(prospect);
            }}
            title="Edit prospect"
          >
            <Edit2 size={14} />
          </button>
        </div>
      </div>
      
      <div className="prospect-card-body">
        <h3 className="prospect-name">
          {prospect.first_name} {prospect.last_name}
        </h3>
        <p className="prospect-email">{prospect.email}</p>
        
        {prospect.instagram && (
          <p className="prospect-instagram">
            <Instagram size={12} />
            @{prospect.instagram.replace('@', '')}
          </p>
        )}
      </div>
      
      <div className="prospect-card-footer">
        <LinkedBadge prospect={prospect} />
        <span className="prospect-date">
          <Calendar size={12} />
          {formatDate(prospect.created_at)}
        </span>
      </div>
    </div>
  );

  // Prospect row for list view
  const ProspectRow = ({ prospect }) => (
    <tr 
      className={`prospect-row ${selectedProspects.has(prospect.id) ? 'selected' : ''}`}
      onClick={() => openEditModal(prospect)}
      style={{ cursor: 'pointer' }}
    >
      <td className="prospect-checkbox-cell" onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          checked={selectedProspects.has(prospect.id)}
          onChange={() => toggleProspectSelection(prospect.id)}
        />
      </td>
      <td className="prospect-name-cell">
        <div className="prospect-name-wrapper">
          <div className="prospect-avatar-small-placeholder">
            {getInitials(prospect)}
          </div>
          <div className="prospect-name-info">
            <span className="prospect-full-name">
              {prospect.first_name} {prospect.last_name}
            </span>
            <div className="prospect-name-meta">
              <LinkedBadge prospect={prospect} />
            </div>
          </div>
        </div>
      </td>
      <td className="prospect-info-cell">
        <div className="prospect-info-stack">
          <span className="prospect-info-primary">
            <Mail size={12} /> {prospect.email}
          </span>
          {prospect.instagram && (
            <a 
              href={`https://instagram.com/${prospect.instagram.replace('@', '')}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()} 
              className="prospect-info-secondary"
            >
              <Instagram size={12} /> @{prospect.instagram.replace('@', '')}
            </a>
          )}
          {prospect.website && (
            <a 
              href={prospect.website.startsWith('http') ? prospect.website : `https://${prospect.website}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()} 
              className="prospect-info-secondary"
            >
              <Globe size={12} /> Website
            </a>
          )}
        </div>
      </td>
      <td className="prospect-note-cell">
        {prospect.note && (
          <span className="prospect-note-preview" title={prospect.note}>
            {prospect.note.length > 50 ? prospect.note.substring(0, 50) + '...' : prospect.note}
          </span>
        )}
      </td>
      <td className="prospect-date-cell">
        {formatDate(prospect.created_at)}
      </td>
      <td className="prospect-actions-cell">
        <div className="row-actions">
          {!prospect.uuid && (
            <button
              className="row-action-btn link-btn"
              onClick={async (e) => {
                e.stopPropagation();
                await handleLinkToUser(prospect);
              }}
              disabled={linkingProspectId === prospect.id}
              title="Link to existing user"
            >
              {linkingProspectId === prospect.id ? (
                <Loader size={14} className="spin" />
              ) : (
                <Link2 size={14} />
              )}
            </button>
          )}
          <button
            className="row-action-btn"
            onClick={(e) => {
              e.stopPropagation();
              openEditModal(prospect);
            }}
            title="Edit"
          >
            <Edit2 size={14} />
          </button>
          <button
            className="row-action-btn danger"
            onClick={(e) => {
              e.stopPropagation();
              setShowDeleteConfirm(prospect);
            }}
            title="Delete"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </td>
    </tr>
  );

  // Add/Edit Modal
  const ProspectFormModal = ({ isEdit = false }) => (
    <div className="modal-overlay" onClick={() => isEdit ? setShowEditModal(false) : setShowAddModal(false)}>
      <div className="modal-content prospect-form-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{isEdit ? 'Edit Prospect' : 'Add New Prospect'}</h2>
          <button 
            className="modal-close-btn"
            onClick={() => isEdit ? setShowEditModal(false) : setShowAddModal(false)}
          >
            <X size={20} />
          </button>
        </div>
        
        <div className="modal-body">
          {formErrors.submit && (
            <div className="form-error-banner">
              <AlertCircle size={16} />
              {formErrors.submit}
            </div>
          )}
          
          <div className="form-section">
            <h3 className="form-section-title">
              <User size={16} />
              Basic Information
            </h3>
            <div className="form-grid">
              <div className="form-group">
                <label>First Name *</label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                  className={formErrors.first_name ? 'input-error' : ''}
                  placeholder="Enter first name"
                />
                {formErrors.first_name && <span className="field-error">{formErrors.first_name}</span>}
              </div>
              <div className="form-group">
                <label>Last Name *</label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, last_name: e.target.value }))}
                  className={formErrors.last_name ? 'input-error' : ''}
                  placeholder="Enter last name"
                />
                {formErrors.last_name && <span className="field-error">{formErrors.last_name}</span>}
              </div>
            </div>
          </div>
          
          <div className="form-section">
            <h3 className="form-section-title">
              <Mail size={16} />
              Contact Details
            </h3>
            <div className="form-group full-width">
              <label>Email Address *</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                className={formErrors.email ? 'input-error' : ''}
                placeholder="email@example.com"
              />
              {formErrors.email && <span className="field-error">{formErrors.email}</span>}
            </div>
            <div className="form-grid">
              <div className="form-group">
                <label>Instagram Handle</label>
                <input
                  type="text"
                  value={formData.instagram}
                  onChange={(e) => setFormData(prev => ({ ...prev, instagram: e.target.value }))}
                  placeholder="@username"
                />
              </div>
              <div className="form-group">
                <label>Website</label>
                <input
                  type="url"
                  value={formData.website}
                  onChange={(e) => setFormData(prev => ({ ...prev, website: e.target.value }))}
                  placeholder="https://example.com"
                />
              </div>
            </div>
          </div>
          
          <div className="form-section">
            <h3 className="form-section-title">
              <Edit2 size={16} />
              Notes
            </h3>
            <div className="form-group full-width">
              <textarea
                value={formData.note}
                onChange={(e) => setFormData(prev => ({ ...prev, note: e.target.value }))}
                placeholder="Add any notes about this prospect..."
                rows={3}
              />
            </div>
          </div>
        </div>
        
        <div className="modal-footer">
          <button 
            className="btn-secondary"
            onClick={() => isEdit ? setShowEditModal(false) : setShowAddModal(false)}
          >
            Cancel
          </button>
          <button 
            className="btn-primary"
            onClick={isEdit ? handleUpdateProspect : handleAddProspect}
            disabled={saving}
          >
            {saving ? (
              <>
                <Loader size={16} className="spin" />
                Saving...
              </>
            ) : (
              <>
                <Save size={16} />
                {isEdit ? 'Update Prospect' : 'Add Prospect'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );

  // Delete confirmation modal
  const DeleteConfirmModal = () => (
    <div className="modal-overlay" onClick={() => setShowDeleteConfirm(null)}>
      <div className="modal-content delete-confirm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="delete-confirm-icon">
          <AlertCircle size={48} />
        </div>
        <h3>Delete Prospect?</h3>
        <p>
          Are you sure you want to delete <strong>{showDeleteConfirm?.first_name} {showDeleteConfirm?.last_name}</strong>?
          This action cannot be undone.
        </p>
        <div className="delete-confirm-actions">
          <button className="btn-secondary" onClick={() => setShowDeleteConfirm(null)}>
            Cancel
          </button>
          <button className="btn-danger" onClick={() => handleDeleteProspect(showDeleteConfirm)}>
            <Trash2 size={16} />
            Delete Prospect
          </button>
        </div>
      </div>
    </div>
  );

  // Communications Panel - inline styles moved outside nested function to prevent focus issues
  const pillStyle = (isSelected) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: '0.25rem',
    padding: '0.375rem 0.75rem',
    fontSize: '0.8125rem',
    border: isSelected ? '2px solid #7c3aed' : '1px solid #d1d5db',
    borderRadius: '0.375rem',
    backgroundColor: isSelected ? '#faf5ff' : 'white',
    color: isSelected ? '#7c3aed' : '#374151',
    fontWeight: isSelected ? '600' : '400',
    cursor: 'pointer',
    transition: 'all 0.15s ease'
  });

  const labelStyle = {
    display: 'block',
    fontSize: '0.875rem',
    fontWeight: '500',
    color: '#374151',
    marginBottom: '0.5rem'
  };

  // Get effective email content (template or custom)
  const getEffectiveEmailContent = () => {
    const template = getSelectedTemplateDetails();
    if (useCustomMessage) {
      return { subject: customSubject, html_body: customHtmlBody };
    }
    return template ? { subject: template.subject, html_body: template.html_body } : null;
  };

  const effectiveContent = getEffectiveEmailContent();
  const communicationsRecipients = getTargetRecipients();

  // Communications Panel JSX
  const renderCommunicationsPanel = () => (
    <div style={{ width: '100%', minWidth: '100%', display: 'block' }}>
        {/* Header */}
        <div style={{ marginBottom: '1.5rem', width: '100%' }}>
          <h2 style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '1.25rem', fontWeight: '600', color: '#111827', margin: 0 }}>
            <MessageSquare size={20} />
            Send Email to Prospects
          </h2>
          <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: '0.25rem 0 0 1.75rem' }}>
            Send templated or custom emails to your prospect list
          </p>
        </div>

        {/* Email Results */}
        {emailResults && (
          <div style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '0.75rem',
            padding: '1rem',
            borderRadius: '0.5rem',
            marginBottom: '1rem',
            position: 'relative',
            background: emailResults.success ? '#ecfdf5' : '#fef2f2',
            border: `1px solid ${emailResults.success ? '#10b981' : '#ef4444'}`,
            color: emailResults.success ? '#065f46' : '#991b1b'
          }}>
            {emailResults.success ? <CheckCircle size={20} /> : <AlertTriangle size={20} />}
            <div>
              <strong style={{ display: 'block', marginBottom: '0.25rem' }}>
                {emailResults.success ? 'Email sent successfully!' : 'Failed to send email'}
              </strong>
              <p style={{ margin: 0, fontSize: '0.875rem' }}>
                {emailResults.success ? (emailResults.message || `Sent to ${emailResults.sent_count || 0} recipient(s)`) : (emailResults.error || 'Unknown error occurred')}
              </p>
            </div>
            <button 
              onClick={() => setEmailResults(null)}
              style={{ position: 'absolute', top: '0.75rem', right: '0.75rem', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit', opacity: 0.7 }}
            >
              <X size={16} />
            </button>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem', width: '100%' }}>
          {/* Email Type */}
          <div style={{ width: '100%' }}>
            <label style={labelStyle}>Email Type</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              <button
                type="button"
                onClick={() => { setUseCustomMessage(false); setShowPreview(false); }}
                style={pillStyle(!useCustomMessage)}
              >
                📋 Use Template
              </button>
              <button
                type="button"
                onClick={() => { setUseCustomMessage(true); setSelectedTemplate(''); setShowPreview(false); }}
                style={pillStyle(useCustomMessage)}
              >
                ✏️ Custom Message
              </button>
            </div>
          </div>

          {/* Template Selection */}
          {!useCustomMessage && (
            <div style={{ width: '100%' }}>
              <label style={labelStyle}>Email Template *</label>
              <select
                value={selectedTemplate}
                onChange={(e) => setSelectedTemplate(e.target.value)}
                style={{
                  width: '100%',
                  padding: '0.5rem 0.75rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  fontSize: '0.875rem',
                  background: 'white'
                }}
              >
                <option value="">-- Select a template --</option>
                {emailTemplates.map(t => (
                  <option key={t.id} value={t.template_key}>
                    {t.template_key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}
                  </option>
                ))}
              </select>
              {loadingTemplates && <span style={{ fontSize: '0.75rem', color: '#6b7280', marginLeft: '0.5rem' }}>Loading...</span>}
              {getSelectedTemplateDetails() && (
                <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem' }}>
                  <strong>Subject:</strong> {getSelectedTemplateDetails().subject}
                </p>
              )}
            </div>
          )}

          {/* Custom Message Editor */}
          {useCustomMessage && (
            <>
              <div style={{ width: '100%' }}>
                <label style={labelStyle}>Subject Line *</label>
                <input
                  type="text"
                  value={customSubject}
                  onChange={(e) => setCustomSubject(e.target.value)}
                  placeholder="Enter email subject..."
                  style={{
                    width: '100%',
                    padding: '0.5rem 0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem'
                  }}
                />
              </div>
              <div style={{ width: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem', width: '100%' }}>
                  <label style={{ ...labelStyle, marginBottom: 0 }}>Email Content *</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <select
                      onChange={(e) => {
                        if (e.target.value) {
                          navigator.clipboard.writeText(e.target.value);
                          e.target.value = '';
                        }
                      }}
                      style={{
                        padding: '0.25rem 0.5rem',
                        fontSize: '0.75rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '0.25rem',
                        background: 'white',
                        cursor: 'pointer'
                      }}
                    >
                      <option value="">+ Insert Field</option>
                      <option value="{first_name}">First Name</option>
                      <option value="{artist_name}">Artist Name</option>
                      <option value="{organization_name}">Organization</option>
                      <option value="{email}">Email</option>
                    </select>
                    <span style={{ fontSize: '0.625rem', color: '#9ca3af' }}>paste where needed</span>
                  </div>
                </div>
                <RichTextEditor
                  value={customHtmlBody}
                  onChange={setCustomHtmlBody}
                  placeholder="Compose your email here..."
                  minHeight="200px"
                  enableImageUpload={true}
                  imageFolder="prospect_emails"
                />
              </div>
            </>
          )}

          {/* Target Audience */}
          <div style={{ width: '100%' }}>
            <label style={labelStyle}>Target Audience</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              <button type="button" onClick={() => setTargetAudience('all')} style={pillStyle(targetAudience === 'all')}>
                All Prospects ({stats.total})
              </button>
              <button type="button" onClick={() => setTargetAudience('unlinked')} style={pillStyle(targetAudience === 'unlinked')}>
                Pure Prospects ({stats.unlinked})
              </button>
              <button type="button" onClick={() => setTargetAudience('linked')} style={pillStyle(targetAudience === 'linked')}>
                Linked Users ({stats.linked})
              </button>
            </div>
          </div>

          {/* Send Mode */}
          <div style={{ width: '100%' }}>
            <label style={labelStyle}>Send Mode</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
              <button
                type="button"
                onClick={() => setTestMode(true)}
                style={{
                  ...pillStyle(testMode),
                  borderColor: testMode ? '#eab308' : '#d1d5db',
                  backgroundColor: testMode ? '#fefce8' : 'white',
                  color: testMode ? '#a16207' : '#374151'
                }}
              >
                🧪 Test Mode
              </button>
              <button
                type="button"
                onClick={() => setTestMode(false)}
                style={{
                  ...pillStyle(!testMode),
                  borderColor: !testMode ? '#22c55e' : '#d1d5db',
                  backgroundColor: !testMode ? '#f0fdf4' : 'white',
                  color: !testMode ? '#15803d' : '#374151'
                }}
              >
                🚀 Production ({communicationsRecipients.length})
              </button>
            </div>
            {testMode && (
              <div style={{ marginTop: '0.75rem' }}>
                <span style={{ fontSize: '0.75rem', fontWeight: '500', color: '#374151' }}>Test Recipients:</span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', marginTop: '0.375rem' }}>
                  {orgMembers.map(member => (
                    <label key={member.id} style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', fontSize: '0.75rem', cursor: 'pointer' }}>
                      <input
                        type="checkbox"
                        checked={selectedTestAdminIds.includes(member.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedTestAdminIds(prev => [...prev, member.id]);
                          } else {
                            setSelectedTestAdminIds(prev => prev.filter(id => id !== member.id));
                          }
                        }}
                        style={{ accentColor: '#7c3aed' }}
                      />
                      <span style={{ color: '#7c3aed' }}>{member.users?.first_name}</span>
                      <span style={{ color: '#9ca3af' }}>({member.role})</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb', width: '100%' }}>
            {(effectiveContent || useCustomMessage) && (
              <button
                onClick={() => setShowPreview(!showPreview)}
                disabled={useCustomMessage && (!customSubject.trim() || !customHtmlBody.trim())}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.375rem',
                  padding: '0.5rem 1rem',
                  fontSize: '0.875rem',
                  border: '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  background: 'white',
                  cursor: 'pointer'
                }}
              >
                <Eye size={16} />
                {showPreview ? 'Hide Preview' : 'Preview'}
              </button>
            )}
            <button
              onClick={handleSendEmail}
              disabled={
                sendingEmail || 
                (testMode && selectedTestAdminIds.length === 0) ||
                (!useCustomMessage && !selectedTemplate) ||
                (useCustomMessage && (!customSubject.trim() || !customHtmlBody.trim()))
              }
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.375rem',
                padding: '0.5rem 1rem',
                fontSize: '0.875rem',
                border: 'none',
                borderRadius: '0.375rem',
                background: '#7c3aed',
                color: 'white',
                cursor: 'pointer',
                opacity: (sendingEmail || (testMode && selectedTestAdminIds.length === 0) || (!useCustomMessage && !selectedTemplate) || (useCustomMessage && (!customSubject.trim() || !customHtmlBody.trim()))) ? 0.5 : 1,
                marginLeft: 'auto'
              }}
            >
              {sendingEmail ? <Loader size={16} className="spin" /> : <Send size={16} />}
              {sendingEmail ? 'Sending...' : (testMode ? 'Send Test' : `Send to ${communicationsRecipients.length}`)}
            </button>
          </div>

          {/* Email Preview */}
          {showPreview && effectiveContent && (
            <div style={{ border: '1px solid #e5e7eb', borderRadius: '0.5rem', overflow: 'hidden' }}>
              <div style={{ padding: '0.75rem 1rem', background: '#e5e7eb', fontSize: '0.875rem', fontWeight: '500' }}>Preview</div>
              <div style={{ padding: '0.75rem 1rem', background: '#f9fafb', borderBottom: '1px solid #e5e7eb', fontSize: '0.875rem' }}>
                <strong>Subject:</strong> {effectiveContent.subject}
              </div>
              <div 
                style={{ padding: '1rem', background: 'white', maxHeight: '300px', overflowY: 'auto' }}
                dangerouslySetInnerHTML={{ __html: getPreviewHtml(effectiveContent.html_body) }}
              />
            </div>
          )}
        </div>
      </div>
  );

  return (
    <div className="prospects-admin">
      {/* Header */}
      <div className="prospects-header">
        <div className="header-content">
          <h1 className="prospects-title">Artist Prospects</h1>
          <p className="prospects-subtitle">Manage potential artists before they create accounts</p>
        </div>
        
        {/* Tab Navigation */}
        <div className="prospects-tabs">
          <button
            className={`tab-btn ${activeTab === 'prospects' ? 'active' : ''}`}
            onClick={() => setActiveTab('prospects')}
          >
            <Users size={16} />
            Prospects
          </button>
          <button
            className={`tab-btn ${activeTab === 'communications' ? 'active' : ''}`}
            onClick={() => setActiveTab('communications')}
          >
            <Mail size={16} />
            Communications
          </button>
        </div>
      </div>

      {/* Communications Tab */}
      {activeTab === 'communications' ? (
        renderCommunicationsPanel()
      ) : (
        <>
          {/* Prospects Tab Content */}
          <div className="prospects-toolbar">
            <div className="toolbar-left">
              <button 
                className="refresh-button-icon"
                onClick={loadProspects}
                title="Refresh prospects"
              >
                <RefreshCw size={16} className={loading ? 'spin' : ''} />
              </button>
              
              {/* Download Dropdown */}
              <div className="download-dropdown-container" ref={downloadDropdownRef}>
                <button 
                  className="btn-secondary"
                  onClick={() => setShowDownloadDropdown(!showDownloadDropdown)}
                  disabled={exporting || filteredProspects.length === 0}
                  title="Download prospects"
                >
                  {exporting ? (
                    <Loader size={16} className="spin" />
                  ) : (
                    <Download size={16} />
                  )}
                  Download
                </button>
                {showDownloadDropdown && (
                  <div className="download-dropdown-menu">
                    <button onClick={exportToCSV} disabled={exporting}>
                      <Download size={14} />
                      Download CSV
                    </button>
                    <button onClick={exportToExcel} disabled={exporting}>
                      <Download size={14} />
                      Download Excel
                    </button>
                  </div>
                )}
              </div>
              
              <button className="btn-primary" onClick={() => setShowAddModal(true)}>
                <UserPlus size={16} />
                Add Prospect
              </button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="prospects-stats">
            <button 
              className={`stat-card clickable ${linkedFilter === 'all' ? 'active' : ''}`}
              onClick={() => setLinkedFilter('all')}
            >
              <div className="stat-icon">
                <Users size={20} />
              </div>
              <div className="stat-info">
                <span className="stat-number">{stats.total}</span>
                <span className="stat-label">Total Prospects →</span>
              </div>
            </button>
            <button 
              className={`stat-card clickable ${linkedFilter === 'linked' ? 'active' : ''}`}
              onClick={() => setLinkedFilter('linked')}
            >
              <div className="stat-icon green">
                <UserCheck size={20} />
              </div>
              <div className="stat-info">
                <span className="stat-number">{stats.linked}</span>
                <span className="stat-label">Linked to Users →</span>
              </div>
            </button>
            <button 
              className={`stat-card clickable ${linkedFilter === 'unlinked' ? 'active' : ''}`}
              onClick={() => setLinkedFilter('unlinked')}
            >
              <div className="stat-icon orange">
                <User size={20} />
              </div>
              <div className="stat-info">
                <span className="stat-number">{stats.unlinked}</span>
                <span className="stat-label">Pure Prospects →</span>
              </div>
            </button>
          </div>

          {/* Controls */}
          <div className="prospects-controls">
            <div className="controls-row">
              <div className="search-container">
                <Search size={16} className="search-icon" />
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search prospects..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
                {searchQuery && (
                  <button 
                    className="search-clear"
                    onClick={() => setSearchQuery('')}
                  >
                    <X size={14} />
                  </button>
                )}
              </div>
            </div>
            
            <div className="controls-row filters-row">
              <div className="icon-filters">
                <div 
                  className="filter-dropdown"
                  onMouseEnter={() => setActiveDropdown('sort')}
                  onMouseLeave={() => setActiveDropdown(null)}
                >
                  <button 
                    className="icon-filter-btn"
                    title="Sort by"
                  >
                    <ArrowUpDown size={16} />
                  </button>
                  {activeDropdown === 'sort' && (
                    <div className="filter-dropdown-menu">
                      <button className={sortBy === 'last_name' ? 'active' : ''} onClick={() => setSortBy('last_name')}>Name</button>
                      <button className={sortBy === 'email' ? 'active' : ''} onClick={() => setSortBy('email')}>Email</button>
                      <button className={sortBy === 'created_at' ? 'active' : ''} onClick={() => setSortBy('created_at')}>Date Added</button>
                    </div>
                  )}
                </div>
                
                <button
                  className="icon-filter-btn"
                  onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                  title={sortOrder === 'asc' ? 'Sort ascending' : 'Sort descending'}
                >
                  {sortOrder === 'asc' ? '↑' : '↓'}
                </button>
                
                <div className="view-toggle">
                  <button
                    className={`view-toggle-btn ${viewMode === 'grid' ? 'active' : ''}`}
                    onClick={() => setViewMode('grid')}
                    title="Grid view"
                  >
                    <Grid size={16} />
                  </button>
                  <button
                    className={`view-toggle-btn ${viewMode === 'list' ? 'active' : ''}`}
                    onClick={() => setViewMode('list')}
                    title="List view"
                  >
                    <ListIcon size={16} />
                  </button>
                </div>
              </div>
            </div>
            
            {/* Filter summary line */}
            <div className="filter-summary">
              <span className="filter-summary-text">
                Showing: <strong>{filteredProspects.length}</strong> {filteredProspects.length === 1 ? 'prospect' : 'prospects'}
                {linkedFilter !== 'all' && (
                  <span className="filter-tag">
                    {linkedFilter === 'linked' ? 'Linked Users' : 'Pure Prospects'}
                    <button onClick={() => setLinkedFilter('all')}><X size={10} /></button>
                  </span>
                )}
                {searchQuery && (
                  <span className="filter-tag">
                    "{searchQuery}"
                    <button onClick={() => setSearchQuery('')}><X size={10} /></button>
                  </span>
                )}
                <span className="sort-info">
                  sorted by {sortBy === 'last_name' ? 'name' : sortBy === 'created_at' ? 'date added' : sortBy} {sortOrder === 'asc' ? '↑' : '↓'}
                </span>
              </span>
            </div>
          </div>

          {/* Bulk actions bar */}
          {selectedProspects.size > 0 && (
            <div className="bulk-actions-bar">
              <span className="selection-count">
                {selectedProspects.size} prospect{selectedProspects.size !== 1 ? 's' : ''} selected
              </span>
              <div className="bulk-actions">
                <button 
                  className="bulk-action-btn danger"
                  onClick={() => setSelectedProspects(new Set())}
                >
                  <X size={14} />
                  Clear Selection
                </button>
              </div>
            </div>
          )}

          {/* Error state */}
          {error && (
            <div className="error-banner">
              <AlertCircle size={16} />
              <span>{error}</span>
              <button onClick={() => setError(null)}>
                <X size={14} />
              </button>
            </div>
          )}

          {/* Loading state */}
          {loading ? (
            <div className="loading-state">
              <Loader size={32} className="spin" />
              <p>Loading prospects...</p>
            </div>
          ) : filteredProspects.length === 0 ? (
            <div className="empty-state">
              <Users size={48} />
              <h3>{searchQuery || linkedFilter !== 'all' ? 'No Matching Prospects' : 'No Prospects Yet'}</h3>
              <p>
                {searchQuery || linkedFilter !== 'all' 
                  ? 'Try adjusting your search or filters.'
                  : 'Add your first prospect to get started.'}
              </p>
              {!searchQuery && linkedFilter === 'all' && (
                <button 
                  className="btn-primary"
                  onClick={() => {
                    resetForm();
                    setShowAddModal(true);
                  }}
                >
                  <UserPlus size={16} />
                  Add First Prospect
                </button>
              )}
            </div>
          ) : (
            <>
              {/* Grid View */}
              {viewMode === 'grid' && (
                <div className="prospects-grid">
                  {paginatedProspects.map(prospect => (
                    <ProspectCard key={prospect.id} prospect={prospect} />
                  ))}
                </div>
              )}

              {/* List View */}
              {viewMode === 'list' && (
                <div className="prospects-table-wrapper">
                  <table className="prospects-table">
                    <thead>
                      <tr>
                        <th className="checkbox-header">
                          <input
                            type="checkbox"
                            checked={selectedProspects.size === paginatedProspects.length && paginatedProspects.length > 0}
                            onChange={toggleSelectAll}
                          />
                        </th>
                        <th>Name</th>
                        <th>Contact</th>
                        <th>Notes</th>
                        <th>Added</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedProspects.map(prospect => (
                        <ProspectRow key={prospect.id} prospect={prospect} />
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination">
                  <div className="pagination-info">
                    Showing {((currentPage - 1) * itemsPerPage) + 1} - {Math.min(currentPage * itemsPerPage, filteredProspects.length)} of {filteredProspects.length}
                  </div>
                  <div className="pagination-controls">
                    <button
                      className="pagination-btn"
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft size={16} />
                    </button>
                    
                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let page;
                      if (totalPages <= 5) {
                        page = i + 1;
                      } else if (currentPage <= 3) {
                        page = i + 1;
                      } else if (currentPage >= totalPages - 2) {
                        page = totalPages - 4 + i;
                      } else {
                        page = currentPage - 2 + i;
                      }
                      return (
                        <button
                          key={page}
                          className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                          onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </button>
                      );
                    })}
                    
                    <button
                      className="pagination-btn"
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                  <div className="pagination-size">
                    <select
                      value={itemsPerPage}
                      onChange={(e) => {
                        setItemsPerPage(Number(e.target.value));
                        setCurrentPage(1);
                      }}
                    >
                      <option value={10}>10 per page</option>
                      <option value={25}>25 per page</option>
                      <option value={50}>50 per page</option>
                      <option value={100}>100 per page</option>
                    </select>
                  </div>
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Modals - always available */}
      {showAddModal && <ProspectFormModal isEdit={false} />}
      {showEditModal && <ProspectFormModal isEdit={true} />}
      {showDeleteConfirm && <DeleteConfirmModal />}
    </div>
  );
};

export default ProspectsAdmin;
